<template>
  <div>
    <div class="curved flex h-fit items-center justify-center fixed w-full z-10 top-0 2xl:pb-5 2xl:px-4 2xl:mt-0 mt-[-6px]">
      <!-- Left Edge (Start) - Flipped -->

      <Eadge 
        class="curved-icon-start relative z-[2]  2xl:h-[71px] h-[79px] -right-[1px]  w-auto fill-white dark:fill-gray-800" 
        :class="{ '-scale-x-100': isRTL }"
      /> 


      <!-- Header Container -->
      <header
        class="light:bg-content-theme flex justify-between items-center h-[70px] 2xl:mt-[-1px] w-full relative bg-white dark:bg-gray-800"
        style="z-index: 1;"
      >
        <Button
          icon="bars-3"
          class="2xl:hidden ml-1"
          variant="action"
          @click.prevent="toggleMainMenu"
          :aria-label="__('Toggle Sidebar')"
          :aria-expanded="mainMenuShown ? 'true' : 'false'"
        />

        <div class="hidden h-[56px] w-[18rem] mr-[25px] bg-white dark:bg-gray-800 shrink-0 2xl:flex items-center px-4">
          <div class="flex items-center gap-3 w-full">
            <img
              v-if="currentUser?.avatar"
              :alt="fullName"
              :src="currentUser.avatar"
              class="rounded-full w-10 h-10 object-cover"
            />
            <img
              v-else
              :alt="fullName"
              :src="'/images/user.png'"
              class="rounded-full w-10 h-10 object-cover"
            />
            <div class="flex flex-col min-w-0">
              <span class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {{ fullName }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex flex-1 items-center px-3 sm:px-8 md:px-3 md:bg-white dark:md:bg-gray-800 md:h-[56px]">
          <GlobalSearch
            class="relative"
            v-if="globalSearchEnabled"
            dusk="global-search-component"
          />

          <div class="isolate relative flex items-center pl-6 ml-auto">
            <!-- <LanguageDropdown /> -->
            <ThemeDropdown />
            <div class="relative z-50">
              <NotificationCenter v-if="notificationCenterEnabled" />
            </div>
            <div class="relative z-[40] hidden md:flex ml-2">
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <!-- Right Edge (End) -->
      <Eadge 
        class="curved-icon-end relative -left-[1px] z-[2] 2xl:h-[71px] h-[79px] w-auto fill-white dark:fill-gray-800" 
        :class="{ '-scale-x-100': !isRTL }"
      />
      
    </div>

    <!-- Mobile Sidebar -->
    <teleport to="body">
      <transition
        enter-active-class="transition-opacity duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-75"
        leave-active-class="transition-opacity duration-300 ease-in"
        leave-from-class="opacity-75"
        leave-to-class="opacity-0"
      >
        <div
          v-if="mainMenuShown"
          @click="toggleMainMenu"
          class="2xl:hidden fixed inset-0 bg-gray-600 dark:bg-gray-700 z-50 opacity-50"
          aria-hidden="true"
        />
      </transition>

      <transition
        enter-active-class="transition-transform duration-300 ease-out"
        :enter-from-class="isRTL ? 'transform translate-x-full' : 'transform -translate-x-full'"
        :enter-to-class="isRTL ? 'transform translate-x-0' : 'transform -translate-x-0'"
        leave-active-class="transition-transform duration-300 ease-in"
        :leave-from-class="isRTL ? 'transform translate-x-0' : 'transform -translate-x-0'"
        :leave-to-class="isRTL ? 'transform translate-x-full' : 'transform -translate-x-full'"
      >
        <div
          v-if="mainMenuShown"
          ref="modalContent"
          :class="`2xl:hidden bg-sidebar fixed inset-y-0 z-50 flex flex-col max-w-xxs w-full left-0`"
          style="will-change: transform;"
        >
          <!-- Close Button -->
          <div class="absolute top-0 right-0 -mr-12 pt-2">
            <button
              @click.prevent="toggleMainMenu"
              class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              :aria-label="__('Close Sidebar')"
            >
              <!-- Heroicon name: outline/x -->
              <svg
                class="h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div class="px-2 bg-sidebar">
            <Link
              :href="$url('/')"
              class="text-gray-900 hover:text-gray-500 active:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:active:text-gray-500 h-12 px-2 rounded-lg flex items-center focus:ring focus:ring-inset focus:outline-none"
              :aria-label="appName"
            >
              <img src="/images/logo.png" class="h-[40px] w-full">
              <!-- <AppLogo /> -->
            </Link>
          </div>

          <div
            class="flex flex-col gap-2 justify-between h-full bg-sidebar py-3 px-3 overflow-x-auto"
          >
            <div class="py-1">
              <MainMenu data-screen="responsive" :menu-height.sync="sidebarMenuHeight-50" />
            </div>
            <div class="mt-auto">
              <MobileUserMenu />
            </div>
          </div>

          <div class="shrink-0 w-14" aria-hidden="true">
            <!-- Dummy element to force sidebar to shrink to fit close icon -->
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { Button } from 'laravel-nova-ui'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue'
import { useLocalization } from '../composables/useLocalization'

const { __ } = useLocalization()

const store = useStore()

const modalContent = ref(null)

const isRTL = computed(() => Nova.config('rtlEnabled'))

const { activate, deactivate } = useFocusTrap(modalContent, {
  initialFocus: true,
  allowOutsideClick: false,
  escapeDeactivates: false,
})

const toggleMainMenu = () => store.commit('toggleMainMenu')

const globalSearchEnabled = computed(() => Nova.config('globalSearchEnabled'))

const notificationCenterEnabled = computed(() =>
  Nova.config('notificationCenterEnabled')
)

const mainMenuShown = computed(() => store.getters.mainMenuShown)
const appName = computed(() => Nova.config('appName'))
const currentUser = computed(() => store.getters.currentUser)

const fullName = computed(() => {
  if (currentUser.value?.last_name && currentUser.value?.first_name) {
    return `${currentUser.value.first_name} ${currentUser.value.last_name}`
  } else if (currentUser.value?.first_name) {
    return currentUser.value.first_name
  } else if (currentUser.value?.username) {
    return currentUser.value.username
  }
  return __('Admin User')
})

const sidebarMenuHeight = ref(0)
let observer

watch(
  () => mainMenuShown.value,
  async (isOpen) => {
    if (!isOpen) {
      // بستن منو
      if (observer && modalContent.value) {
        observer.unobserve(modalContent.value)
      }
      deactivate()
      return
    }

    // باز شدن منو
    await nextTick()

    if (!modalContent.value) return

    observer = new ResizeObserver(entries => {
      const height = entries[0].contentRect.height

      sidebarMenuHeight.value =
        height - 40 - 50 - 96
    })

    observer.observe(modalContent.value)
    activate()
  }
)


watch(
  () => mainMenuShown.value,
  newValue => {
    if (newValue === true) {
      document.body.classList.add('overflow-y-hidden')
      Nova.pauseShortcuts()
      return
    }

    document.body.classList.remove('overflow-y-hidden')
    Nova.resumeShortcuts()
    deactivate()
  }
)

onBeforeUnmount(() => {
  document.body.classList.remove('overflow-hidden')
  Nova.resumeShortcuts()
  deactivate()
})

onUnmounted(() => {
  if (observer && modalContent.value) {
    observer.unobserve(modalContent.value)
  }
})

</script>
